import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { Copy, Save, Wand2, Mail, CheckCircle2, Send } from "lucide-react";
import { EmailSendConfirm } from "./focus/EmailSendConfirm";

// Email draft (subject + body): edit/save, AI rewrite with a note, copy or open in the
// mail app, then log it as emailed (sending via M365 comes later).
export const EmailDraftPane = ({ id, email, sl, paused, onLogged }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState({ subject: "", body: "" });
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (regenerate = false, n = "") => {
    setLoading(true);
    const d = await sl.getDraft(id, regenerate, n, "email");
    setLoading(false);
    if (d) { setSubject(d.subject || ""); setBody(d.body || ""); setSaved({ subject: d.subject || "", body: d.body || "" }); }
  };
  useEffect(() => { setSubject(""); setBody(""); setNote(""); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = subject.trim() !== saved.subject.trim() || body.trim() !== saved.body.trim();
  // Outlook on the web compose link (mailto: does nothing when no desktop mail app is set up).
  const owa = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const toReview = async () => {
    try {
      const r = (await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/smart-leads/email/outlook-review`, { lead_id: id, subject: subject.trim(), body: body.trim() })).data;
      toast.success(`Draft sent to ${r.sent_to} for review`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send to Outlook"); }
  };

  return (
    <div className="space-y-2" data-testid="email-draft-pane">
      <div className="text-[11px] text-slate-500">To: <b data-testid="email-draft-to">{email}</b></div>
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={loading} maxLength={150}
        placeholder={loading ? "Drafting…" : "Subject"} data-testid="email-draft-subject" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} disabled={loading} maxLength={3000}
        placeholder={loading ? "Writing a personal email…" : ""} data-testid="email-draft-body" />
      <div className="flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell AI what to add" className="h-8 text-[12px]" data-testid="email-draft-note" />
        <Button size="sm" variant="outline" className="h-8 shrink-0 border-amber-300 text-amber-700" disabled={loading}
          onClick={async () => { await load(true, note.trim()); setNote(""); }} data-testid="email-draft-rewrite-btn">
          <Wand2 className="h-3 w-3 mr-1" />{note.trim() ? "Rewrite" : "New draft"}
        </Button>
        <Button size="sm" variant="outline" className="h-8 shrink-0" disabled={!dirty || !subject.trim() || !body.trim() || sl.busy === "save"}
          onClick={async () => { const r = await sl.saveEmailDraft(id, subject.trim(), body.trim()); if (r) setSaved({ subject: r.subject, body: r.body }); }}
          data-testid="email-draft-save-btn">
          <Save className="h-3 w-3 mr-1" />Save
        </Button>
      </div>
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" disabled={!body} onClick={() => { navigator.clipboard?.writeText(`Subject: ${subject}\n\n${body}`); toast.success("Email copied"); }} data-testid="email-draft-copy-btn">
          <Copy className="h-4 w-4 mr-1.5" />Copy
        </Button>
        <Button variant="outline" size="sm" disabled={!body} onClick={() => window.open(owa, "_blank", "noopener")} data-testid="email-draft-open-btn">
          <Mail className="h-4 w-4 mr-1.5" />Open in Outlook
        </Button>
        <Button variant="outline" size="sm" className="border-blue-300 text-blue-700" disabled={!body.trim() || loading} onClick={toReview} data-testid="email-draft-review-btn">
          <Send className="h-4 w-4 mr-1.5" />Send to my Outlook for review
        </Button>
        <EmailSendConfirm leadId={id} subject={subject.trim()} body={body.trim()} disabled={paused || loading || !body.trim() || !subject.trim()} onSent={() => onLogged?.()} />
        <Button size="sm" className="bg-gradient-to-r from-violet-500 to-indigo-600" disabled={paused || loading || !body.trim() || !!sl.busy}
          onClick={async () => { const r = await sl.logAttempt(id, "email", `${subject}\n\n${body}`.slice(0, 320)); if (r) onLogged?.(); }}
          data-testid="email-draft-mark-sent-btn">
          <CheckCircle2 className="h-4 w-4 mr-1.5" />Mark as emailed
        </Button>
      </div>
    </div>
  );
};
